
import React, { useState } from 'react';
import { useSuppliers } from './hooks/useSuppliers';

const SupplierList: React.FC = () => {
  const { suppliers, loading, error } = useSuppliers();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('business_name');

  const filteredSuppliers = suppliers?.filter(supplier => 
    supplier.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contact_person_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.city.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(supplier => 
    categoryFilter === '' || supplier.category === categoryFilter
  ).sort((a, b) => {
    if (sortBy === 'overall_rating') return b.overall_rating - a.overall_rating;
    if (sortBy === 'established_year') return b.established_year - a.established_year;
    return a[sortBy as keyof typeof a]?.toString().localeCompare(b[sortBy as keyof typeof b]?.toString() || '') || 0;
  });

  const categories = [...new Set(suppliers?.map(s => s.category) || [])];

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800">Error loading suppliers: {error.message}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Supplier Management</h1>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
          <i className="ri-add-line mr-2"></i>
          Add New Supplier
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="business_name">Business Name</option>
              <option value="overall_rating">Overall Rating</option>
              <option value="established_year">Established Year</option>
              <option value="city">City</option>
            </select>
          </div>

          <div className="flex items-end">
            <button 
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('');
                setSortBy('business_name');
              }}
              className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredSuppliers?.map((supplier) => (
          <div key={supplier.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{supplier.business_name}</h3>
                  <p className="text-sm text-gray-600">{supplier.category}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(supplier.status)}`}>
                  {supplier.status}
                </span>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <i className="ri-user-line w-4 h-4 mr-2"></i>
                  {supplier.contact_person_name}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <i className="ri-phone-line w-4 h-4 mr-2"></i>
                  {supplier.contact_person_phone}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <i className="ri-map-pin-line w-4 h-4 mr-2"></i>
                  {supplier.city}, {supplier.state}
                </div>
              </div>

              {/* Ratings */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center">
                  <div className={`text-lg font-bold ${getRatingColor(supplier.overall_rating)}`}>
                    {supplier.overall_rating.toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500">Overall</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold ${getRatingColor(supplier.quality_rating)}`}>
                    {supplier.quality_rating.toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500">Quality</div>
                </div>
              </div>

              {/* Rating Breakdown */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-xs">
                  <span>Punctuality</span>
                  <span className={getRatingColor(supplier.punctuality_rating)}>
                    {supplier.punctuality_rating.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>Tech Capability</span>
                  <span className={getRatingColor(supplier.tech_capability_rating)}>
                    {supplier.tech_capability_rating.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>Pricing</span>
                  <span className={getRatingColor(supplier.pricing_rating)}>
                    {supplier.pricing_rating.toFixed(1)}
                  </span>
                </div>
              </div>

              {/* Business Info */}
              <div className="text-xs text-gray-500 mb-4">
                <div>Est. {supplier.established_year}</div>
                <div>Turnover: ₹{(supplier.annual_turnover / 10000000).toFixed(1)}Cr</div>
                <div>Credit: ₹{(supplier.credit_limit / 100000).toFixed(1)}L</div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg text-sm hover:bg-blue-100 transition-colors">
                  <i className="ri-eye-line mr-1"></i>
                  View Details
                </button>
                <button className="flex-1 bg-green-50 text-green-600 px-3 py-2 rounded-lg text-sm hover:bg-green-100 transition-colors">
                  <i className="ri-shopping-cart-line mr-1"></i>
                  Place Order
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Supplier Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{suppliers?.length || 0}</div>
            <div className="text-sm text-gray-500">Total Suppliers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {suppliers?.filter(s => s.status === 'active').length || 0}
            </div>
            <div className="text-sm text-gray-500">Active Suppliers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {suppliers?.filter(s => s.overall_rating >= 4.5).length || 0}
            </div>
            <div className="text-sm text-gray-500">Top Rated (4.5+)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{categories.length}</div>
            <div className="text-sm text-gray-500">Categories</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierList;
